import { useState } from 'react';

import { SwapHorizOutlined } from '@mui/icons-material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, ButtonBase, Collapse, Divider, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { AccountSearchField } from './AccountSearchField';
import { MENU_ACCOUNT_LIMIT, needsFullList, visibleEntries } from './accountSwitcherLabels';
import { AccountSwitcherRow } from './AccountSwitcherRow';
import { useSwitcherEntries } from './useSwitcherEntries';

const customStyles = {
  // `ButtonBase` and not `Typography`: the popover traps the Tab cycle, so a `<p>`
  // with an onClick leaves the selector unreachable by keyboard. With two accounts
  // the rows sit inside a closed `Collapse` and this is the only door, so without
  // it there is no way to switch accounts without a mouse.
  toggle: {
    fontSize: '14px',
    fontWeight: '400',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    padding: '6px 12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      color: (theme) => theme.palette.primary.main,
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px 4px 0',
    width: '280px',
  },
  title: {
    fontSize: '14px',
    fontWeight: '600',
    padding: '0 6px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  empty: {
    fontSize: '13px',
    color: '#6E7178',
    padding: '6px 10px',
  },
  seeAll: {
    fontSize: '14px',
    fontWeight: '500',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    color: (theme) => theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  },
  iconSize: {
    fontSize: '16px',
  },
  divider: {
    marginTop: '4px',
  },
  // Reserves the height of the collapsed entry plus its divider. The switcher
  // renders nothing while the list is loading, and without this the items
  // already painted below jump down when the answer lands, right over Log out.
  skeletonRow: {
    height: '28px',
    margin: '2px 10px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
} satisfies Record<string, SxProps<Theme>>;

interface Props {
  /** The account currently being switched to, from `useAccountSwitch`. */
  switchingTo: number | null;
  onSwitch: (userId: number, accountName: string) => void;
  /** Opens the full list, which lives above the popover so it can outlive it. */
  onSeeAllAccounts: () => void;
}

/**
 * Account selector inside the user menu.
 *
 * Nothing is rendered when the person has no linked accounts, which is almost
 * everyone: whoever works in a single account notices no change at all.
 *
 * Two shapes, and the account count decides which. Up to five accounts the
 * whole set fits behind the old collapsible entry, so it stays there and out of
 * the way of a menu that is mostly used for other things. Past five the list
 * stops being scannable and switching accounts is clearly what the person came
 * for, so it becomes the main block of the menu: search field, the first five
 * accounts and a way into the complete list.
 *
 * Accounts are ALWAYS listed by name, never by email: the email of a secondary
 * account carries the internal variant and is not shown to the user.
 *
 * Switching itself lives in `useAccountSwitch`, above the popover that unmounts
 * this component when the menu closes.
 */
export function AccountSwitcher({ switchingTo, onSwitch, onSeeAllAccounts }: Props) {
  const lang = useLanguage();
  const { entries, currentUserId, total, isLoading } = useSwitcherEntries();

  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const copy = lang.mainTopBar.accountSwitcher;

  // A collapsed shell rather than nothing: going from null to the whole block
  // pushed the items below it down, and Log out is the one under the pointer. The
  // exact height cannot be reserved without knowing `total` first, so this is the
  // one for the common case. An account without the feature never asks, so it
  // never reserves either.
  if (isLoading) {
    return (
      <>
        <Box
          sx={customStyles.toggle}
          data-testid='account-switcher-loading'
        >
          <SwapHorizOutlined
            fontSize='inherit'
            color='inherit'
            sx={customStyles.iconSize}
          />
          {copy.title}
        </Box>
        <Box sx={customStyles.skeletonRow} />
      </>
    );
  }

  if (!entries.length) return null;

  if (!needsFullList(total)) {
    const rows = visibleEntries(entries, '', { pinnedUserId: currentUserId });

    return (
      <>
        <ButtonBase
          sx={customStyles.toggle}
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          data-testid='account-switcher-toggle'
        >
          <SwapHorizOutlined
            fontSize='inherit'
            color='inherit'
            sx={customStyles.iconSize}
          />
          {copy.title}
        </ButtonBase>
        <Collapse in={expanded}>
          <Box sx={customStyles.list}>
            {rows.map((entry) => (
              <AccountSwitcherRow
                key={entry.user_id}
                entry={entry}
                isCurrent={entry.user_id === currentUserId}
                isSwitching={switchingTo === entry.user_id}
                currentAccountLabel={copy.currentAccount}
                avatarSize={24}
                onSelect={onSwitch}
              />
            ))}
          </Box>
        </Collapse>
      </>
    );
  }

  const rows = visibleEntries(entries, search, {
    limit: MENU_ACCOUNT_LIMIT,
    pinnedUserId: currentUserId,
  });

  return (
    <>
      <Box sx={customStyles.block}>
        <Typography sx={customStyles.title}>{copy.title}</Typography>
        <AccountSearchField
          value={search}
          onChange={setSearch}
          placeholder={copy.searchPlaceholder}
          buttonLabel={copy.searchAction}
          testId='account-switcher-search'
        />
        <Box sx={customStyles.list}>
          {rows.length === 0 && (
            <Typography
              sx={customStyles.empty}
              data-testid='account-switcher-no-results'
            >
              {copy.noResults}
            </Typography>
          )}
          {rows.map((entry) => (
            <AccountSwitcherRow
              key={entry.user_id}
              entry={entry}
              isCurrent={entry.user_id === currentUserId}
              isSwitching={switchingTo === entry.user_id}
              currentAccountLabel={copy.currentAccount}
              onSelect={onSwitch}
            />
          ))}
        </Box>
        <ButtonBase
          sx={customStyles.seeAll}
          onClick={onSeeAllAccounts}
          data-testid='account-switcher-see-all'
        >
          <SearchRoundedIcon
            fontSize='inherit'
            color='inherit'
            sx={customStyles.iconSize}
          />
          {copy.seeAllAccounts.replace('{{count}}', String(total))}
        </ButtonBase>
      </Box>
      <Divider sx={customStyles.divider} />
    </>
  );
}
